// janus-engine/rolls/random-table.ts — Lane N2 RT-2 (2026-10-01): RANDOM TABLES RESOLVE IN THE BROWSER (owner 2026-09-30, late:
// "the random tables need to get built or compiled into the encounterArr now … so the roll_random_table works independently of
// PHP"). The twin of random_table_roll() / handle_random_table_item_id_attached()
// (includes/functions/roll_types/random_table_roll/random_table_roll_inc.php), pure: the caller hands it the table definitions
// (a catalog def carrying the `random_table_ranges` bucket — random_table_catalog_inc.php — or the object_inc.php fetch) and a die
// source; it returns every log row the legacy roll wrote, the faces each one drew, and the resolved range. PHP records them
// (options/game/actions/roll_truth_commit_inc.php kind `table`) — it never looks the range up again.
//
// Semantics (legacy parity, oracle: tools/probes/random_table_oracle.php → tools/test/janus-random-table.test.mjs):
//   - the authored pools random_table_roll_dice/_die/_mod_{1,2} are rolled as roll_dice_arr() each and SUMMED; no pool → 1d(max end).
//   - the range is the first bucket row covering the result: the bucket is ordered end DESC, range_i DESC — the legacy
//     `… BETWEEN begin AND end ORDER BY roll_range_end DESC LIMIT 1`. (Soft-deleted rows are not in the bucket: a deliberate fix.)
//   - an attached RANDOM TABLE rolls next (roll_type_2 `subset_table_roll`), its own range snapshot on its own row, and so on down;
//     an attached CREATURE with quantity dice on its range rolls the quantity (roll_type_1 `random_creature`). Nothing spawns.
//   - adv/dis: random_table_roll() only ever reads a table's own `roll_type` layer (none is authored, 2026-10-01) — such a table is
//     refused here (`unsupported:roll_type`) until that accumulation is ported.

import { buildRollDiceArr, drawFaces, faceCount, mathRandomDie, MAX_FACES, RANDOM_TABLE_KINDS, type DieSource, type DiceTuple } from './dice-roll.js';

type Row = Record<string, unknown>;
export { RANDOM_TABLE_KINDS };

/** One live range row of the bucket (random_table_catalog_inc.php ft_random_table_ranges_bucket()). */
export interface RandomTableRange {
  begin: number; end: number; effect?: string; description?: string; attached?: string; attached_type?: string;
  qty_dice?: number; qty_die?: number; qty_mod?: number;
}
export interface RandomTablePool { dice: number; die: number; mod: number }
/** `authored`: the table carries the random_table_roll_dice_1 layer (legacy `layer_has`) — its valid pools are rolled; else 1d(max end). */
export interface RandomTableDef { id: string; itemType: string; name: string; authored: boolean; pools: RandomTablePool[]; ranges: RandomTableRange[]; rollType: string | null }

/** One roll the legacy chain inserted (all rows share one roll_id). `pools` carry the faces each pool drew, in draw order. */
export interface RandomTableRollStep {
  roll_type_1: string; roll_type_2: string; roll_item_id: string;
  pools: Array<RandomTablePool & { faces: number[] }>;
  dice: number; die: number; roll_mod: number; adv_dis_stacks: number;
  roll_result: number; rolls_full: string;
  /** roll_meta.random_table — the resolved range snapshot (null on a quantity row, which legacy never snapshotted). */
  random_table: { effect: string | null; description: string | null; attached_item_id: string | null } | null;
}
export interface RandomTableRollResult {
  steps: RandomTableRollStep[];
  /** The TOP-LEVEL range (what random_table_roll() returned to the ability dispatcher). */
  range: RandomTableRange | null;
  attached: string | null; attachedType: string | null;
}
export type RandomTableRollOutcome = { ok: true; result: RandomTableRollResult } | { ok: false; need: string } | { ok: false; refuse: string };

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const upper = (v: unknown): string => String(v ?? '').trim().toUpperCase();
const isObj = (v: unknown): v is Row => !!v && typeof v === 'object' && !Array.isArray(v);
const int = (v: unknown): number => { const n = Number(v); return Number.isFinite(n) ? Math.trunc(n) : 0; };
/** First scalar of a layer slot (descalared scalar, a [value] list, or a shell's item id). */
function slot(def: Row, key: string): unknown {
  const ilt = isObj(def.item_layer_types) ? def.item_layer_types : null;
  let v: unknown = ilt ? ilt[key] : undefined;
  if (Array.isArray(v)) v = v[0];
  if (isObj(v)) v = isObj(v.items) ? v.items.item_id : v.item_id ?? v.item_layer_effect;
  return v;
}
const itemsCol = (def: Row, key: string): unknown => (isObj(def.items) ? def.items[key] : undefined) ?? def[key];

/**
 * A table definition (a catalog entry or the object_inc.php payload) → the resolver's shape. Null = not a random table, or it
 * carries no `random_table_ranges` bucket (a stale compile / a private table: fetch it per viewer).
 */
export function readRandomTableDef(id: string, def: unknown): RandomTableDef | null {
  if (!isObj(def) || !Array.isArray(def.random_table_ranges)) return null;
  const itemType = String(itemsCol(def, 'item_type') ?? '');
  if (!RANDOM_TABLE_KINDS.has(itemType)) return null;
  const pools: RandomTablePool[] = [];
  for (const n of [1, 2]) {
    const dice = int(slot(def, `random_table_roll_dice_${n}`)), die = int(slot(def, `random_table_roll_die_${n}`));
    if (dice > 0 && die > 0) pools.push({ dice, die, mod: int(slot(def, `random_table_roll_mod_${n}`)) });
  }
  const ranges: RandomTableRange[] = [];
  for (const r of def.random_table_ranges) {
    if (!isObj(r) || !Number.isFinite(Number(r.begin)) || !Number.isFinite(Number(r.end))) continue;
    const row: RandomTableRange = { begin: int(r.begin), end: int(r.end) };
    if (typeof r.effect === 'string' && r.effect !== '') row.effect = r.effect;
    if (typeof r.description === 'string' && r.description !== '') row.description = r.description;
    if (UUID.test(String(r.attached ?? ''))) { row.attached = upper(r.attached); if (typeof r.attached_type === 'string' && r.attached_type) row.attached_type = r.attached_type; }
    for (const k of ['qty_dice', 'qty_die', 'qty_mod'] as const) if (int(r[k]) !== 0) row[k] = int(r[k]);
    ranges.push(row);
  }
  const rollType = slot(def, 'roll_type');
  const authored = slot(def, 'random_table_roll_dice_1') !== undefined && slot(def, 'random_table_roll_dice_1') !== null;
  return { id: upper(id), itemType, name: String(itemsCol(def, 'item_name') ?? ''), authored, pools, ranges,
    rollType: rollType === undefined || rollType === null || rollType === '' ? null : String(rollType) };
}

/** The legacy lookup: the first row (bucket order = end DESC, range_i DESC) with begin ≤ value ≤ end. */
export function lookupRandomTableRange(ranges: readonly RandomTableRange[], value: number): RandomTableRange | null {
  for (const r of ranges) if (r.begin <= value && value <= r.end) return r;
  return null;
}
/** The no-pool die: the largest roll_range_end (legacy: `ORDER BY roll_range_end DESC LIMIT 1`). 0 when the table has no rows. */
export function randomTableMaxEnd(ranges: readonly RandomTableRange[]): number {
  let max = 0;
  for (const r of ranges) if (r.end > max) max = r.end;
  return max;
}

/** One authored-or-max roll: roll_dice_arr() per pool, summed (roll_authored_random_table_dice / janus_default_roll_inc). */
export function rollRandomTablePools(pools: readonly RandomTablePool[], adv: number, source: DieSource): Pick<RandomTableRollStep, 'pools' | 'dice' | 'die' | 'roll_mod' | 'roll_result' | 'rolls_full'> | null {
  let result = 0, dice = 0, mod = 0;
  const sizes = new Set<number>();
  const tuples: DiceTuple[] = [];
  const out: Array<RandomTablePool & { faces: number[] }> = [];
  for (const p of pools) {
    const faces = drawFaces(p.dice, p.die, adv, source);
    const arr = buildRollDiceArr(p.dice, p.die, p.mod, adv, faces);
    if (!arr) return null;
    result += arr[0]![0]; dice += p.dice; mod += p.mod; sizes.add(p.die);
    tuples.push(...arr.slice(2));
    out.push({ ...p, faces });
  }
  return { pools: out, dice, die: sizes.size === 1 ? [...sizes][0]! : 0, roll_mod: mod, roll_result: result, rolls_full: JSON.stringify(tuples) };
}

const snapshot = (r: RandomTableRange | null): NonNullable<RandomTableRollStep['random_table']> =>
  ({ effect: r?.effect ?? null, description: r?.description ?? null, attached_item_id: r?.attached ?? null });
/** The most rows one table roll may chain (legacy has no cap; a cycle through two tables would recurse forever). */
export const MAX_TABLE_CHAIN = 12;

/**
 * Roll one table (random_table_roll() + the handle_random_table_item_id_attached() cascade). `defs` answers a table id with its
 * definition, or null when it is not held — the outcome is then `{need: id}` and the caller fetches it and rolls AGAIN with the
 * same tape (pass a replaying source) so the draws already made are kept.
 */
export function rollRandomTable(tableId: string, defs: (id: string) => RandomTableDef | null, source: DieSource = mathRandomDie, adv = 0): RandomTableRollOutcome {
  const top = defs(upper(tableId));
  if (!top) return { ok: false, need: upper(tableId) };
  if (top.rollType !== null) return { ok: false, refuse: 'unsupported:roll_type' };
  const steps: RandomTableRollStep[] = [];
  let faces = 0;
  const rollTable = (def: RandomTableDef, rollType2: string): { step: RandomTableRollStep; range: RandomTableRange | null } | string => {
    const pools = def.authored ? def.pools : [{ dice: 1, die: randomTableMaxEnd(def.ranges), mod: 0 }];
    if (!pools.length || pools.some(p => p.die <= 0)) return 'empty-table';
    faces += pools.reduce((s, p) => s + faceCount(p.dice, p.die, adv), 0);
    if (faces > MAX_FACES) return 'too-many-faces';
    const rolled = rollRandomTablePools(pools, adv, source);
    if (!rolled) return 'faces';
    const range = lookupRandomTableRange(def.ranges, rolled.roll_result);
    return { range, step: { roll_type_1: def.itemType, roll_type_2: rollType2, roll_item_id: def.id, ...rolled, adv_dis_stacks: adv, random_table: snapshot(range) } };
  };
  const first = rollTable(top, '');
  if (typeof first === 'string') return { ok: false, refuse: first };
  steps.push(first.step);
  // handle_random_table_item_id_attached(): `owner` is the table whose range attached `range.attached`. The TOP range is followed
  // even when it attaches its own table (a "roll again" row: legacy re-rolls once); below that, a range attaching the table it
  // came from ends the chain (legacy: `$resolved_attached_id !== $nested_table_id`).
  let owner = top, range = first.range;
  while (range?.attached && (steps.length === 1 || range.attached !== owner.id)) {
    const type = range.attached_type ?? '';
    // The cap guards only a row about to be ADDED (Codex review 2026-10-01: a terminal attachment after the last row is fine).
    const addsRow = RANDOM_TABLE_KINDS.has(type) || (type === 'creature' && (range.qty_dice ?? 0) > 0 && (range.qty_die ?? 0) > 0);
    if (addsRow && steps.length >= MAX_TABLE_CHAIN) return { ok: false, refuse: 'chain-too-long' };
    if (RANDOM_TABLE_KINDS.has(type)) {
      const nested = defs(range.attached);
      if (!nested) return { ok: false, need: range.attached };
      if (nested.rollType !== null) return { ok: false, refuse: 'unsupported:roll_type' };
      const next = rollTable(nested, 'subset_table_roll');
      if (next === 'empty-table') break; // handle_random_table_item_id_attached(): a nested table with no rows ends the chain silently
      if (typeof next === 'string') return { ok: false, refuse: next };
      steps.push(next.step);
      owner = nested; range = next.range;
      continue;
    }
    if (type === 'creature' && (range.qty_dice ?? 0) > 0 && (range.qty_die ?? 0) > 0) {
      const qty = { dice: range.qty_dice!, die: range.qty_die!, mod: range.qty_mod ?? 0 };
      faces += faceCount(qty.dice, qty.die, adv);
      if (faces > MAX_FACES) return { ok: false, refuse: 'too-many-faces' };
      const rolled = rollRandomTablePools([qty], adv, source);
      if (!rolled) return { ok: false, refuse: 'faces' };
      steps.push({ roll_type_1: 'random_creature', roll_type_2: 'subset_table_roll', roll_item_id: owner.id, ...rolled, adv_dis_stacks: adv, random_table: null });
    }
    break;
  }
  return { ok: true, result: { steps, range: first.range, attached: first.range?.attached ?? null, attachedType: first.range?.attached_type ?? null } };
}

/** A die source that replays `tape` first, then draws (and records) from `fallback` — a `{need}` retry keeps the faces it drew. */
export function tapedDieSource(tape: number[], fallback: DieSource = mathRandomDie): DieSource {
  let i = 0;
  return die => { if (i < tape.length) return tape[i++]!; const f = fallback(die); tape.push(f); i++; return f; };
}
